import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { LanguageService } from '../../../../../core/services/language.service';
import { ToastNotifService } from '../../../../../core/services/toastNotif.service';
import { ToastCustomService } from '../../../../../tools/services/toast/toast-custom/toastCustom.service';
import { ApiEvChargerService } from '../../../../../core/services/api/Ev-chager/api-Ev-Carger';
import { ChargingJobsHistory } from '../../../../../core/models/ev.model';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';

@Component({
  selector: 'app-ev-charger-history',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PssLoadingComponent
  ],
  templateUrl: './ev-charger-history.component.html',
  styleUrl: './ev-charger-history.component.css',
})
export class EvChargerHistoryComponent implements OnInit {
  currentLang: 'TH' | 'EN' = 'TH';
  token: string = '';
  uid: string = '';
  evChargerHistory: ChargingJobsHistory[] | null = null;
  isLoading: boolean = false;

  // Search & Filter States
  searchTerm: string = '';
  selectedFilter: 'ALL' | '7DAYS' | '30DAYS' = 'ALL';
  sortDirection: 'DESC' | 'ASC' = 'DESC';

  // Detail Modal State
  selectedJobDetail: ChargingJobsHistory | null = null;
  showDetailModal: boolean = false;

  constructor(
    private languageService: LanguageService,
    private router: Router,
    private apiEvChargerService: ApiEvChargerService,
    private toast: ToastNotifService,
    private toastCustomService: ToastCustomService,
    private cookieService: CookieService
  ) {
    this.languageService.currentLang$.subscribe((lang) => {
      this.currentLang = lang;
    });
    this.uid =
      this.cookieService.get('line_uid') ||
      localStorage.getItem('line_uid') ||
      '';
    this.token = '';
  }

  ngOnInit(): void {
    setTimeout(() => {
      this.getChargingJobsHistory();
    }, 10);
  }

  /*#################################################### Function ################################################*/
  // ================= Normal Functions =================

  /**
   * คำอธิบาย: คำนวณชั่วโมงจากจำนวนนาทีทั้งหมด
   */
  hours(evCharger: number): number {
    return Math.floor((evCharger || 0) / 60);
  }

  /**
   * คำอธิบาย: คำนวณนาทีที่เหลือหลังจากหักชั่วโมงออกแล้ว
   */
  minutes(evCharger: number): number {
    return (evCharger || 0) % 60;
  }

  /**
   * คำอธิบาย: จัดรูปแบบระยะเวลาชาร์จแสดงเป็นข้อความภาษาไทย/อังกฤษ (เช่น 2 ชม. 15 นาที)
   */
  formatDuration(totalMinutes: number | null | undefined): string {
    const mins = totalMinutes || 0;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h > 0) {
      return this.currentLang === 'TH' ? `${h} ชม. ${m} นาที` : `${h} hr ${m} min`;
    }
    return this.currentLang === 'TH' ? `${m} นาที` : `${m} min`;
  }

  /**
   * คำอธิบาย: เปลี่ยนหมวดหมู่ตัวกรองประวัติการชาร์จ (ทั้งหมด / 7 วัน / 30 วัน)
   */
  setFilter(filter: 'ALL' | '7DAYS' | '30DAYS'): void {
    this.selectedFilter = filter;
  }

  /**
   * คำอธิบาย: สลับการเรียงลำดับวันจากล่าสุดไปเก่าสุด หรือเก่าสุดไปล่าสุด
   */
  toggleSort(): void {
    this.sortDirection = this.sortDirection === 'DESC' ? 'ASC' : 'DESC';
  }

  /**
   * คำอธิบาย: ล้างข้อความการค้นหา
   */
  clearSearch(): void {
    this.searchTerm = '';
  }

  /**
   * คำอธิบาย: เปิด Modal สรุปรายละเอียดรายการชาร์จ
   */
  openDetail(job: ChargingJobsHistory): void {
    this.selectedJobDetail = job;
    this.showDetailModal = true;
  }

  /**
   * คำอธิบาย: ปิด Modal รายละเอียด
   */
  closeDetail(): void {
    this.showDetailModal = false;
    this.selectedJobDetail = null;
  }

  /**
   * คำอธิบาย: แสดง Toast แจ้งเตือนข้อความเตือนภัย
   */
  showWarning(text: string): void {
    this.toastCustomService.showWarning('', text);
  }

  /**
   * คำอธิบาย: แสดง Toast แจ้งเตือนข้อผิดพลาด
   */
  showError(text: string): void {
    this.toastCustomService.showError('', text);
  }

  /**
   * คำอธิบาย: แสดง Toast แจ้งเตือนความสำเร็จ
   */
  showSuccess(text: string): void {
    this.toastCustomService.showSuccess('', text);
  }

  /**
   * คำอธิบาย: ย้อนกลับไปยังหน้าหลัก EV Charger
   */
  goback(): void {
    this.router.navigate(['/ev-charger']);
  }

  /**
   * Getter คำอธิบาย: ดึงรายการประวัติการชาร์จที่ผ่านการค้นหา กรอง และเรียงลำดับแล้ว
   */
  get filteredEvChargerHistory(): ChargingJobsHistory[] {
    if (!this.evChargerHistory) return [];

    let result = [...this.evChargerHistory];

    // Filter by date range
    const now = new Date().getTime();
    if (this.selectedFilter === '7DAYS') {
      const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
      result = result.filter((item) => {
        const itemDate = new Date(item.createDate || item.chargingStart || 0).getTime();
        return itemDate >= sevenDaysAgo;
      });
    } else if (this.selectedFilter === '30DAYS') {
      const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;
      result = result.filter((item) => {
        const itemDate = new Date(item.createDate || item.chargingStart || 0).getTime();
        return itemDate >= thirtyDaysAgo;
      });
    }

    // Search filter
    if (this.searchTerm.trim() !== '') {
      const term = this.searchTerm.toLowerCase().trim();
      result = result.filter((item) => {
        const plate = (item.licensePlate || '').toLowerCase();
        const site = (item.siteName || '').toLowerCase();
        const space = (item.spaceCode || '').toLowerCase();
        const job = (item.jobId || '').toLowerCase();
        return plate.includes(term) || site.includes(term) || space.includes(term) || job.includes(term);
      });
    }

    // Sort by date
    result.sort((a, b) => {
      const dateA = new Date(a.createDate || a.chargingStart || 0).getTime();
      const dateB = new Date(b.createDate || b.chargingStart || 0).getTime();
      return this.sortDirection === 'DESC' ? dateB - dateA : dateA - dateB;
    });

    return result;
  }

  /**
   * Getter คำอธิบาย: ยอดรวมค่าบริการชาร์จทั้งหมดของรายการที่กรอง (บาท)
   */
  get totalAmount(): number {
    return this.filteredEvChargerHistory.reduce(
      (sum, item) => sum + (item.chargingAmount || 0),
      0
    );
  }

  /**
   * Getter คำอธิบาย: ปริมาณพลังงานรวมทั้งหมดของรายการที่กรอง (kWh)
   */
  get totalKwh(): number {
    return this.filteredEvChargerHistory.reduce(
      (sum, item) => sum + (item.chargingKwh || 0),
      0
    );
  }

  /**
   * Getter คำอธิบาย: จำนวนครั้งการชาร์จทั้งหมดของรายการที่กรอง
   */
  get totalSessions(): number {
    return this.filteredEvChargerHistory.length;
  }

  /**
   * Getter คำอธิบาย: ระยะเวลาการชาร์จสะสมทั้งหมดของรายการที่กรอง (นาที)
   */
  get totalDurationMinutes(): number {
    return this.filteredEvChargerHistory.reduce(
      (sum, item) => sum + (item.totalTimeMinutes || 0),
      0
    );
  }

  /*################################------------- API -------------#################################*/
  // ================= API Functions =================

  /**
   * คำอธิบาย: เรียกดึงข้อมูลประวัติการชาร์จรถยนต์ไฟฟ้าของผู้ใช้งานจากเซิร์ฟเวอร์
   */
  getChargingJobsHistory(): void {
    console.log("getChargingJobsHistory : ", this.uid, this.token)
    this.isLoading = true;
    this.apiEvChargerService
      .getChargingJobsHistory(this.uid, this.token)
      .subscribe({
        next: (response: ChargingJobsHistory[]) => {
          console.log("ChargingJobsHistory : ", response)
          this.isLoading = false;
          this.evChargerHistory = response;
        },
        error: (err) => {
          this.isLoading = false;
          console.error('เกิดข้อผิดพลาดในการดึงประวัติการชาร์จ', err);
          this.showError(
            this.currentLang === 'TH'
              ? 'โหลดข้อมูลล้มเหลว'
              : 'Failed to load data.'
          );
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }
}
